import { useMemo } from 'react'
import { useProject } from '@/lib/db/rxdb/hooks/entities/projects'

export type StatusMode = 'simple' | 'advanced'

/**
 * Returns the status mode for a given project.
 * New projects default to 'simple'.
 * Tasks without a projectId default to 'simple' (personal tasks).
 */
export function useProjectStatusMode(projectId: string | null | undefined): {
  statusMode: StatusMode
  isAdvanced: boolean
  isLoading: boolean
} {
  const { project, isLoading } = useProject(projectId ?? undefined)

  const statusMode: StatusMode = useMemo(() => {
    if (!projectId) return 'simple'
    if (!project) return 'simple'
    return project.statusMode ?? 'simple'
  }, [projectId, project])

  return {
    statusMode,
    isAdvanced: statusMode === 'advanced',
    isLoading,
  }
}

/**
 * Synchronous helper for when you already have the project document.
 */
export function getStatusModeFromProject(
  project: { statusMode?: 'simple' | 'advanced' | null } | null | undefined,
): StatusMode {
  return project?.statusMode ?? 'simple'
}
